import 'bootstrap/dist/css/bootstrap.css';
import { Modal } from 'bootstrap';

import * as THREE from "three";
import { Timer } from 'three/examples/jsm/misc/Timer';

import { Globe } from "./globe";
import { Satellite } from "./satellite";
import { Coin } from "./coin";
import { Scoreboard } from "./scoreboard";
import { TumblingCollisionObject } from './collision_object';
import { Flame } from './flame';

// I discovered that Chrome on Windows doesn't actually display the
// flag emojis! These might be most of our users unfortunately...
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
if(polyfillCountryFlagEmojis()){
  console.log("Flags polyfilled");
}
else{
  console.log("Flags not polyfilled");
}


function ordinal_suffix_of(i) {
    let j = i % 10,
        k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
}

class Game{
  camera : THREE.Camera;
  scene : THREE.Scene;
  renderer: THREE.Renderer;

  globe : Globe;
  satellite : Satellite;
  gravity_multiplier : number;

  coins : Array<Coin>;
  coin_timer : Timer;
  seconds_since_last_coin : number;
  min_coin_height : number;

  coin_coint : number;
  score : number;
  last_level_up_score : number;

  collision_objects : Array<TumblingCollisionObject>;
  collision_object_timer: Timer;
  seconds_since_last_collision_object : number;

  control_div: Element | null;

  scoreboard : Scoreboard;

  flame: Flame;

  game_over: boolean;

  onWindowResize() {

    const canvas = this.renderer.domElement;

    // @ts-ignore
    // this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
    // @ts-ignore
    this.camera.updateProjectionMatrix();

    this.renderer.setSize( window.innerWidth, window.innerHeight - this.control_div?.clientHeight );
  }

  constructor(){
    this.scoreboard = new Scoreboard( this.set_game_over );
    this.score = 0;
    this.coins = [];
    this.collision_objects = [];
    this.scene = new THREE.Scene();
    this.last_level_up_score = 0;
    this.camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    this.control_div = document.querySelector("#controls");

    this.scene.background = new THREE.Color( 0x000000 );
    // const helper = new THREE.GridHelper( 160, 10, 0x8d8d8d, 0xc1c1c1 );
    // helper.rotation.x = Math.PI / 2;
    // this.scene.add( helper );

    this.camera.position.y = 25;
    this.camera.position.z = 28;
    // this.camera.lookAt(0,0,0);


    this.globe = new Globe();
    this.globe.mesh.position.y = -2;
    this.scene.add(this.globe.mesh);

    this.satellite = new Satellite(this.scene);
    this.satellite.add_to_scene();

    this.coin_timer = new Timer();
    this.min_coin_height = 40;
    const coin = new Coin(this.scene, this.scoreboard.increment_score);
    coin.add_to_scene(45, this.min_coin_height);
    this.coins.push(coin);
    this.seconds_since_last_coin = 0;

    this.collision_object_timer = new Timer();
    this.seconds_since_last_collision_object = 0;

    console.log(this.satellite);

    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
    const canvas = document.body;
    canvas?.appendChild(this.renderer.domElement);
    // @ts-ignore
    this.renderer.setPixelRatio( window.devicePixelRatio );
    
    this.renderer.setSize( window.innerWidth, window.innerHeight - this.control_div?.clientHeight );

    this.gravity_multiplier = this.calc_gravity_multiplier();

    this.flame = new Flame(this.scene, this.scoreboard.decrement_and_draw_remaining_lives);

    window.addEventListener( 'resize', () => this.onWindowResize());
    window.requestAnimationFrame((millis_since_last) => this.animate(millis_since_last))

    document.addEventListener("keydown", this.handle_key_down);
    document.addEventListener("touchstart", this.handle_touch);
  }

  set_game_over = () => {
    // Ensure that any intervals are cancelled
    clearInterval(this.scoreboard.interval_id);
    clearInterval(this.flame.interval_id);
    this.flame.hide();
    if(! this.game_over){
      this.game_over = true;
      const game_over_message_element = document.getElementById("game-over-message") as HTMLElement
      game_over_message_element.innerHTML = `
      Your score was:  ${this.scoreboard.score}<br />`
      this.scoreboard.update_server(this.scoreboard.score).then(response => response.json()).then(data => {
          const ranking_str = ordinal_suffix_of(data.ranking + 1);
          game_over_message_element.innerHTML += `
          You ranked: ${ranking_str} out of ${data.n_sessions} players overall`

          console.log(data)
          if(data.hasOwnProperty("country_code")){
            // At some point I learned that innerHTML is bad but keeping it here
            // for consistency...
            const country_ranking_str = ordinal_suffix_of(data.country_ranking + 1);
            game_over_message_element.innerHTML += `,
            and ${country_ranking_str} in <span style='font-family: "Twemoji Country Flags"'>${data.flag_emoji_char}</span>.<p />`
          }
          else{
            game_over_message_element.innerHTML += ".<p />";
          }

        });

      this.scoreboard.request_country_leaderboard().then(response => response.json()).then(data => {
        game_over_message_element.innerHTML += `
        <center>
        <table class="table">
          <thead>
            <tr>
              <th scope="col">Ranking</th>
              <th scope="col">Country</th>
              <th scope="col">Total Score</th>
            </tr>
          </thead>
          <tbody id="scoreboard_content">
          </tbody>
        </table>
        </center>
      `;
      const scoreboard_content_element = document.getElementById("scoreboard_content");
      for(const country_ranking of data.ordered_scoreboard){
        console.log(country_ranking)
        scoreboard_content_element.innerHTML += `<tr>
          <th scope="row">${country_ranking.rank}</th>
          <td style='font-family: "Twemoji Country Flags"'>${country_ranking.flag_emoji_char}</td>
          <td>${country_ranking.total_score}</td>
        </tr>`;
      }
    });


      const game_over_element = document.getElementById('game-over-modal') as HTMLElement;
      const game_over_modal = new Modal(game_over_element, {backdrop: 'static', keyboard: false});

      game_over_modal.show();

    } 
  }

  calc_gravity_multiplier(){
    const normalised_satellite_distance = (this.satellite.group.position.y - 32) / 45;
    const gravity_multiplier = 1.8 ** (9 * (normalised_satellite_distance * -1));
    return gravity_multiplier
  }

  animate(millis_since_last : number){
    const step = () =>{
      this.gravity_multiplier = this.calc_gravity_multiplier();

      this.globe.rotate(this.gravity_multiplier);
      this.satellite.animate(this.gravity_multiplier, this.flame);

      // Handle the coins
      const coins_still_in_play : Array<Coin> = [];
      for(const coin of this.coins){
        coin.animate(this.gravity_multiplier);
        if(coin.claim_or_delete(this.satellite.group.position.y)){
          this.scene.remove(coin.pivot);
        }
        else{
          coins_still_in_play.push(coin);
        }
      }

      const collision_objects_still_in_play : Array<TumblingCollisionObject> = [];
      for(const collision_object of this.collision_objects){
        // console.log(collision_object);
        collision_object.animate();


        if(collision_object.did_collide(this.satellite.group.position)){
          this.scoreboard.decrement_and_draw_remaining_lives();
          collision_object.remove_from_scene();
        }
        else if(collision_object.out_of_play()){
          collision_object.remove_from_scene();
        }
        else{
          collision_objects_still_in_play.push(collision_object);
        }
      }

      this.coins = coins_still_in_play;
      this.collision_objects = collision_objects_still_in_play;

      this.coin_timer.update();
      this.seconds_since_last_coin += this.coin_timer.getDelta();
      if(this.seconds_since_last_coin >= 2){
        this.seconds_since_last_coin = 0;
        
        const new_coin = new Coin(this.scene, this.scoreboard.increment_score);
        new_coin.add_to_scene(30, Math.max(this.min_coin_height, 32));
        this.coins.push(new_coin);

        // Random bonus coin if we're doing well!
        if(this.min_coin_height < 38 && Math.random() >= 0.7){
          const bonus_coin = new Coin(this.scene, this.scoreboard.increment_score);
          bonus_coin.add_to_scene(30, Math.max(this.min_coin_height, 32));
          this.coins.push(bonus_coin);
          
        }

        if(this.scoreboard.score !== this.last_level_up_score && Math.abs(this.scoreboard.score - this.last_level_up_score) >= 400){
          this.last_level_up_score = this.scoreboard.score;
          console.log("Level up!");
          this.min_coin_height -= 2;
        }
      }

      // Handle the collision objects
      this.collision_object_timer.update();
      this.seconds_since_last_collision_object += this.collision_object_timer.getDelta();
      if(this.seconds_since_last_collision_object >= 4){
        this.seconds_since_last_collision_object = 0;
        const new_collision_object = new TumblingCollisionObject(this.scene, this.satellite.group.position);
        this.collision_objects.push(new_collision_object);
      }
      
      this.renderer.render(this.scene, this.camera);
      if(! this.game_over){
        requestAnimationFrame(step);
      }
    }
    step();
  }

  handle_key_down = (event : KeyboardEvent) =>{
    // Space bar = 32
    if(event.keyCode === 32){
      this.satellite.thrust(this.gravity_multiplier);
    }
  }
  handle_touch = (event) => {
    this.satellite.thrust(this.gravity_multiplier);
    event.preventDefault();
  }
}

let app = new Game();
const new_game = () => {
  console.log("Creating new game instance");
  document.body.removeChild(app.renderer.domElement);
  document.removeEventListener("keydown", app.handle_key_down);
  document.removeEventListener("touchstart", app.handle_touch);

  document.getElementById("scoreboard").innerHTML = "000000";
  for(const element of document.getElementsByClassName("life-icon")){
    element.style.visibility = "visible";
  }
  app = new Game();
}
document.getElementById("new-game-button").addEventListener("click", new_game);
